import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import Table from '@/components/table/Table';
import { DATE_YMD_HMS_12H_FORMAT, DEFAULT_SELECT_PAGE_SIZES, SUBMIT_MOMENT_DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { InfoIcon, SearchIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { scrollToTop } from '@/utils/utils';
import Calendar from '@/components/calendar/Calendar';
import { Notification } from '@/types/notification';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import ViewNotificationModal from './ViewNotificationModal';

export default observer(function NotificationManagementPage() {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        notificationStore: { notifications, getAllNotifications, setObservable, paging, totalRecord, defaultPaging },
        modalStore: { showModal }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        keyword: yup
            .string(),
        receiverName: yup
            .string(),
        fromCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        toCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            })
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        keyword: '',
        receiverName: '',
        fromCreatedDate: '',
        toCreatedDate: ''
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    const watchFromSendDate = form.watch('fromCreatedDate');
    const watchToSendDate = form.watch('toCreatedDate');

    // State
    const [searchState, setSearchState] = useState<Object>();

    // lifecycle
    useEffect(() => {
        onFetch();
    }, []);

    // function
    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        getAllNotifications({
            ...searchParams,
            fromCreatedDate: formatDateTime(searchParams?.fromCreatedDate, SUBMIT_MOMENT_DATE_YMD) ?? null,
            toCreatedDate: formatDateTime(searchParams?.toCreatedDate, SUBMIT_MOMENT_DATE_YMD) ?? null
        }, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleClickViewDetail = (data: Notification) => {
        showModal({
            size: '4xl',
            title: t('words_title.notification_detail'),
            content: <ViewNotificationModal code={data.code} />,
            footerClassName: 'p-0',
            footer: <></>
        });
    };

    const handleDisableTime = (date: Date, limitDate: string | Date | undefined, isToDate: boolean) => {
        if (limitDate) {
            return isToDate ? date < limitDate : date > limitDate;
        }
        return false;
    };

    const removeHyperlinkInContent = (str: string): string => {
        if (!str) return '';
        return str.replace('<a class="underline text-link-1"', '<span').replace('</a>', '</span>');
    };

    // columns
    const columns: ColumnDef<Notification>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'content',
            header: t('words_title.content'),
            enableSorting: false,
            size: 350,
            cell: ({ row: { original } }) => (
                <div
                    dangerouslySetInnerHTML={{
                        __html: original?.content ?
                            (
                                language === LANGUAGES_SUPPORTED.English ? removeHyperlinkInContent(original.content.en) : removeHyperlinkInContent(original.content.vi)
                            ) :
                            ''
                    }}
                >
                </div>
            )
        },
        {
            accessorKey: 'reviewer',
            header: t('words_title.receiver'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <>
                    {
                        original?.user ?
                            (
                                original.user?.isDeleted ?
                                    (
                                        <div>{original?.user?.fullName ?? ''}</div>
                                    ) :
                                    (
                                        <Link to={`/users/${original.user.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                            {original?.user?.fullName ?? ''}
                                        </Link>
                                    )
                            ) :
                            (
                                original?.shop?.isDeleted ?
                                    (
                                        <div>{original?.shop?.name ?? ''}</div>
                                    ) :
                                    (
                                        <Link to={`/shops/${original.shop.code}`} target='blank' rel='noopener noreferrer' className='underline text-link-1 cursor-pointer'>
                                            {original?.shop?.name ?? ''}
                                        </Link>
                                    )
                            )
                    }
                </>
            )
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.send_date'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original.createdAt ? formatDateTime(original.createdAt, DATE_YMD_HMS_12H_FORMAT) : ''}</div>
            )
        }
    ];

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>

                            <FormField
                                control={form.control}
                                name='keyword'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.content')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='flex items-center'>
                                <Label className='w-1/5 label-1'>{t('words_title.send_date')}</Label>
                                <div className='flex items-center gap-3 w-4/5'>
                                    <FormField
                                        control={form.control}
                                        name='fromCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchToSendDate, false)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='toCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchFromSendDate, true)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name='receiverName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.receiver')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <div className='bg-white rounded p-4 mt-4'>
                <Table
                    columns={columns}
                    data={notifications}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    state={paging}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                />
            </div>
        </div>
    );
});
